import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getDefaultVisualizationConfigGQL} from 'analysis-tool/ui/charting/getDefaultVisualizationConfig';
import {assert} from 'Asserts';
import ChartContent from 'charts/chart-content/ChartContent';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {
  GetSubsetDataFrame,
  GetSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetSubsetDataFrame';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import {GET_SUBSET_DATA_FRAME_QUERY, DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import PresentationalUniversalBlockView from 'insights/universal-blocks/PresentationalUniversalBlockView';
import ScratchFileChip from 'pipelines/files/ScratchFileChip';
import {renderFilePreview} from 'pipelines/files/ScratchFilePaneContent';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {pipelineStepRawDataIsOfKind} from 'pipelines/StepDataUtils';
import Float from 'ui-library/floating-ui/Float';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {assertNever} from 'util/Types';

import {DEFAULT_FLOAT_PROPS, MAX_POPOVER_HEIGHT, MAX_POPOVER_WIDTH} from './PopoverConstants';

/**
 * Popover contents for a data frame
 */
function DataFramePopoverContents({
  rawData,
}: {
  rawData: PipelineStepRawData<PipelineStepDataKind.DATASET>;
}): JSX.Element {
  return (
    <BenchlingQuery<GetSubsetDataFrame, GetSubsetDataFrameVariables>
      query={GET_SUBSET_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId: rawData.id, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame}) => {
        assert(!isInaccessible(dataFrame), 'Expected data frame to be accessible.');
        return (
          /*
           * A fixed height is required so tables don't expand and are forced to use their internal scrollbar.
           * However, we only apply the fixed height for larger data frames, so there isn't whitespace in the
           * popover beneath small tables.
           */
          <div
            className={css(
              styles.dataFramePreview,
              dataFrame.numRows > 10 && styles.fixedHeightDataFramePreview
            )}
          >
            <PresentationalUniversalBlockView
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              inputDataFrame={dataFrame.dataFrameJson}
              visualizationConfig={getDefaultVisualizationConfigGQL(
                dataFrame.dataFrameJson.columns,
                VisualizationType.TABLE
              )}
              viewStateId={`analysis-flowchart-popover-${rawData.id}`}
              paneProps={{
                banner: (
                  <DataFrameWarningBanner
                    canDownloadCompleteData={false}
                    isRandomSubset={false}
                    name={dataFrame.dataFrameJson.name}
                    renderedColumns={dataFrame.dataFrameJson.columns.length}
                    renderedRows={dataFrame.dataFrameJson.rows.length}
                    totalColumns={dataFrame.allColumnsJson.length}
                    totalRows={dataFrame.numRows}
                  />
                ),
                title: dataFrame.dataFrameJson.name,
              }}
            />
          </div>
        );
      }}
    </BenchlingQuery>
  );
}

/**
 * Popover contents for a chart
 */
function ChartPopoverContents({
  rawData,
}: {
  rawData: PipelineStepRawData<PipelineStepDataKind.CHART>;
}): JSX.Element {
  return (
    <div className={css(styles.fixedDimensionPreview)}>
      <ChartContent chart={rawData} />
    </div>
  );
}

/**
 * Popover contents for a file
 */
function FilePopoverContents({
  rawData,
}: {
  rawData: PipelineStepRawData<PipelineStepDataKind.FILE>;
}): JSX.Element {
  const filePreview = renderFilePreview(rawData);
  return filePreview ? (
    <div className={css(styles.fixedDimensionPreview)}>{filePreview}</div>
  ) : (
    <div className={css(styles.chipPreview)}>
      <ScratchFileChip scratchFile={rawData} />
    </div>
  );
}

/**
 * Popover content to preview a raw step data (a data frame, a chart, or a file).
 */
export function RawDataPopoverContents({rawData}: {rawData: PipelineStepRawData}): JSX.Element {
  if (pipelineStepRawDataIsOfKind(PipelineStepDataKind.DATASET, rawData)) {
    return <DataFramePopoverContents rawData={rawData} />;
  }
  if (pipelineStepRawDataIsOfKind(PipelineStepDataKind.CHART, rawData)) {
    return <ChartPopoverContents rawData={rawData} />;
  }
  if (pipelineStepRawDataIsOfKind(PipelineStepDataKind.FILE, rawData)) {
    return <FilePopoverContents rawData={rawData} />;
  }
  return assertNever(rawData);
}

interface RawStepDataPopoverProps {
  children: JSX.Element;
  rawData: PipelineStepRawData;
}

/**
 * Show a popover to preview a raw step data (a data frame, a chart, or a file).
 */
export default function RawDataPopover({rawData, children}: RawStepDataPopoverProps): JSX.Element {
  return (
    <Float {...DEFAULT_FLOAT_PROPS} renderContent={() => <RawDataPopoverContents rawData={rawData} />}>
      {children}
    </Float>
  );
}

const styles = StyleSheet.create({
  chipPreview: {
    padding: 10,
  },
  dataFramePreview: {
    maxWidth: MAX_POPOVER_WIDTH,
  },
  fixedHeightDataFramePreview: {
    height: MAX_POPOVER_HEIGHT,
    maxWidth: MAX_POPOVER_WIDTH,
  },
  /**
   * Chart and certain file previews will grow to whatever dimensions are provided, so provide fixed
   * dimensions. Center content, so loading throbbers aren't positioned awkwardly in the upper-left.
   */
  fixedDimensionPreview: {
    display: 'flex',
    justifyContent: 'center',
    height: MAX_POPOVER_HEIGHT,
    width: MAX_POPOVER_WIDTH,
  },
});
